import type React from "react";
import { useState, useEffect } from "react";
import { Mail, CheckCircle, AlertCircle, ArrowLeft, Send } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "../../../../services/axiosInstance";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
  const t = setTimeout(() => setIsVisible(true), 80);
  return () => clearTimeout(t);
}, []);

useEffect(() => {
  if (!message && !error) return;

  const t = setTimeout(() => {
    setMessage("");
    setError("");
  }, 5000);

  return () => clearTimeout(t);
}, [message, error]);

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setMessage("");
    setError("");

    if (!email.trim()) {
      setError("L'email est requis");
      return;
    }

    if (!validateEmail(email)) {
      setError("Format d'email invalide");
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post("/api/users/forgot-password", { email });

setMessage(res.data?.message || "Email envoyé avec succès");
 
      setEmail("");
    } catch (err: any) {
  console.error("ERROR:", err?.response);

  if (err?.response?.data?.error) {
    setError(err.response.data.error);
  } else {
    setError("Erreur serveur");
  }
} finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">

       <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
    
      />

      <div
        className={`w-full max-w-[420px] transition-all duration-500 ease-out
          ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      >

         <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.08)] overflow-hidden">

           <div className="h-1 w-full bg-gradient-to-r from-[#0F52BA] via-blue-400 to-blue-200" />

          <div className="px-8 py-8">

             <div className="flex flex-col items-center text-center mb-8">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-[#0F52BA] mb-4 shadow-sm">
                <Mail size={22} />
              </div>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight leading-tight mb-1">
                Mot de passe oublié ?
              </h1>
              <p className="text-sm text-slate-400 leading-relaxed max-w-[260px]">
                Entrez votre adresse email pour recevoir un lien de réinitialisation.
              </p>
            </div>

             <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-xs font-semibold text-slate-600">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
                  />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => {
  setEmail(e.target.value)
  setError("")
  setMessage("")
}}
                    placeholder="votre@email.com"
                    required
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60
                      text-sm text-slate-700 placeholder-slate-300
                      focus:outline-none focus:border-[#0F52BA] focus:ring-2 focus:ring-blue-100 focus:bg-white
                      transition-all duration-150"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
                  bg-[#0F52BA] text-white text-sm font-bold
                  hover:bg-blue-700 active:scale-[0.99]
                  disabled:opacity-60 disabled:cursor-not-allowed
                  transition-all duration-150 shadow-sm shadow-blue-200 mt-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Envoyer le lien
                  </>
                )}
              </button>
            </form>

             {message && (
              <div className="mt-4 flex items-start gap-2.5 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 text-sm font-medium">
                <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                <p className="leading-relaxed">{message}</p>
              </div>
            )}

            {error && (
              <div className="mt-4 flex items-start gap-2.5 px-4 py-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-sm font-medium">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <p className="leading-relaxed">{error}</p>
              </div>
            )}

             <div className="mt-6 pt-5 border-t border-slate-100 flex justify-center">
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0F52BA] transition-colors duration-150"
              >
                <ArrowLeft size={13} />
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>

         <p className="text-center text-[11px] text-slate-400 mt-4">
          Vérifiez également vos spams si vous ne recevez rien.
        </p>

      </div>
    </div>
  );
};

export default ForgotPassword;